import { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '_metronic/partials'
import { formatGrandNumber } from 'Utils/numberFormat'
import formatNumberCommas from 'Utils/formatNumberCommas'

interface StackedPileChartProps {
  categories: string[]
  series: Array<{
    name: string
    data: number[]
  }>
  colors: string[]
  chartId?: string
  chartHeight?: number
  yAxisTitle?: string
  valueUnit?: string
  abbreviateValues?: boolean
  showDataLabels?: boolean
}

const StackedPileChart: React.FC<StackedPileChartProps> = ({
  categories,
  series,
  colors,
  chartId = 'kt_charts_stacked_pile_chart',
  chartHeight = 600,
  yAxisTitle = 'จำนวนต้นเสาเข็ม',
  valueUnit = 'ต้น',
  abbreviateValues = true,
  showDataLabels = true,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(
        categories,
        series,
        colors,
        mode,
        chartHeight,
        yAxisTitle,
        valueUnit,
        abbreviateValues,
        showDataLabels
      )
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [
    chartRef,
    mode,
    categories,
    series,
    colors,
    chartHeight,
    yAxisTitle,
    valueUnit,
    abbreviateValues,
    showDataLabels,
  ])

  return (
    <>
      <div
        ref={chartRef}
        id={chartId}
        style={{ height: `${chartHeight}px` }}
        className='card-rounded-bottom'></div>
    </>
  )
}

function getChartOptions (
  categories: string[],
  series: Array<{ name: string; data: number[] }>,
  colors: string[],
  mode: string,
  chartHeight: number,
  yAxisTitle: string,
  valueUnit: string,
  abbreviateValues: boolean,
  showDataLabels: boolean
): ApexOptions {
  const textColor = mode === 'dark' ? '#A1A5B7' : '#7E8299'

  const formatChartValue = (val: number | string): string | number => {
    const num = typeof val === 'number' ? val : Number(val)
    if (Number.isNaN(num)) {
      return val
    }
    return abbreviateValues ? formatGrandNumber(num) : formatNumberCommas(num)
  }

  return {
    series: series,
    colors: colors,
    chart: {
      type: 'bar',
      height: chartHeight,
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 8,
      },
    },
    dataLabels: {
      enabled: showDataLabels,
      style: {
        colors: ['#000000'],
        fontSize: '10px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 'light',
      },
      offsetY: 0,
      formatter: function (val: number | string): string | number {
        return formatChartValue(val)
      },
      textAnchor: 'middle',
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: textColor,
        },
        rotate: -90,
        rotateAlways: true,
        offsetY: 0,
      },
    },
    yaxis: {
      title: {
        text: yAxisTitle,
        style: {
          color: textColor,
        },
      },
      labels: {
        style: {
          colors: textColor,
        },
        formatter: (val: number) => formatChartValue(val) as string,
      },
    },
    fill: {
      opacity: 1,
    },
    // legend: {
    //   position: 'top',
    //   horizontalAlign: 'right',
    //   labels: {
    //     colors: textColor,
    //     useSeriesColors: false,
    //   },
    //   markers: {
    //     fillColors: colors,
    //     width: 0,
    //     height: 0,
    //   },
    //   // formatter: function (seriesName: string, opts: any) {
    //   //   const series = opts.w.config.series.find((s: any) => s.name === seriesName)
    //   //   const sum = series.data.reduce((a: number, b: number) => a + b, 0)
    //   //   return `<div style="font-weight: bold; font-size: 1.5rem;">${formatGrandNumber(
    //   //     sum
    //   //   )} เสา</div><span style="display: inline-block; width: 12px; height: 12px; margin-right: 5px; border-radius: 3px; background-color: ${
    //   //     opts.w.config.colors[opts.seriesIndex]
    //   //   };"></span>${seriesName}`
    //   // },
    // },
    tooltip: {
      y: {
        formatter: function (val) {
          return `${formatChartValue(val)} ${valueUnit}`
        },
      },
    },
  }
}

export default StackedPileChart
